import type { EntityATI } from '@atlassian/search-client/common/types';

import type { FilterId } from '../common/types/filters';
import type { PlatformIntegrationAri, ThirdPartyConfig } from '../common/types/schemas/types';
import type { ProductKeys3P, ProductResourceTypes3P } from '../common/ui/configs/types';
import { isProductKey3P, ThirdPartyConfigs } from '../common/ui/configs/utils';
import type { RovoLogo } from '../common/ui/icons';
import type { TypeFilterConfig3P } from '../common/utils/filters/universalType';
import { filterIdOrdering } from '../controllers/filters/filter-id-ordering';
import { UNIVERSAL_FILTER_IDS } from '../controllers/filters/universal-filter-ids';

import { ResourceTypeToProductKeyMap } from './resource-type-to-product-key-map';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ResourceTypeToProductKeyMap } from './resource-type-to-product-key-map';

const getProductKey3P = (
	key: ProductKeys3P | ProductResourceTypes3P,
): ProductKeys3P | undefined => {
	if (isProductKey3P(key)) {
		return key;
	}
	return ResourceTypeToProductKeyMap.get(key);
};
/**
 * Main facade for accessing connector configuration.
 * Provides a simple, consistent API for all consumers.
 *
 * Accepts either a ProductKeys3P or ProductResourceTypes3P value
 * and resolves the correct config from the appropriate registry.
 */
class ConnectorsConfig {
	getProductKey(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): ProductKeys3P | undefined {
		return getProductKey3P(productKeyOrResourceType);
	}
	/**
	 * Get the full configuration for a specific product.
	 */
	async getConfig(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<ThirdPartyConfig | undefined> {
		const productKey = getProductKey3P(productKeyOrResourceType);
		if (!productKey) {
			return undefined;
		}
		return await ThirdPartyConfigs.get(productKey);
	}

	/**
	 * Get the display name for a product.
	 */
	async getDisplayName(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<React.ReactNode | undefined> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.displayName;
	}

	/**
	 * Get the icon for a product.
	 */
	async getIcon(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<RovoLogo | undefined> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.icon;
	}

	/**
	 * Get the search result icon for a product.
	 */
	async getSearchResultIcon(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<RovoLogo | undefined> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.searchResultIcon;
	}

	/**
	 * Check if a product is enabled.
	 * Returns false if the product is not found.
	 */
	async isEnabled(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<boolean> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.isEnabled() ?? false;
	}

	/**
	 * Get the integration ARI for a product.
	 */
	async getIntegrationAri(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<PlatformIntegrationAri | undefined> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.integrationAri;
	}

	/**
	 * Get the type filter config for a product.
	 */
	async getTypeFilterConfig(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<TypeFilterConfig3P | undefined> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.typeFilterConfig;
	}

	/**
	 * Get the filters for a product.
	 */
	async getFilters(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<FilterId[] | undefined> {
		const config = await this.getConfig(productKeyOrResourceType);
		return config?.filters;
	}

	/**
	 * Get the available filters for a product.
	 * Returns universal filters merged with product-specific filters, deduped and ordered
	 * by the standard filter ordering.
	 * Returns only universal filters if the product is not found or has no filters.
	 */
	async getAvailableFilters(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<FilterId[]> {
		const filters = (await this.getFilters(productKeyOrResourceType)) ?? [];

		// Merge universal + product-specific, deduplicate
		const filterIdSet = new Set<FilterId>([...UNIVERSAL_FILTER_IDS, ...filters]);

		return filterIdOrdering.filter((id) => filterIdSet.has(id));
	}

	/**
	 * Get all entity ATIs for a product by extracting them from the type filter config.
	 * Returns an empty array if the product is not found or has no type filter config.
	 */
	async getEntitiesForProduct(
		productKeyOrResourceType: ProductKeys3P | ProductResourceTypes3P,
	): Promise<EntityATI[]> {
		const config = await this.getConfig(productKeyOrResourceType);
		if (!config?.typeFilterConfig) {
			return [];
		}
		const entities: EntityATI[] = [];
		for (const [, value] of config.typeFilterConfig) {
			entities.push(...value.entities3P);
		}
		return entities;
	}
}

/**
 * Singleton instance of the ConnectorsConfig.
 */
export const connectorsConfig: ConnectorsConfig = new ConnectorsConfig();
